import React, { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { TILE_GUIDE } from '../../data/tileGuide';
import { GuideIntro, GuideSociety } from './GuideIntro';

const GOLD = '#D4AF37';
const GOLD_HI = '#F5D97A';

const Section = ({ label, text, color }) => (
  <div className="mt-2.5">
    <div className="font-['Outfit'] uppercase" style={{ fontSize: 9, letterSpacing: '0.24em', color }}>{label}</div>
    <p className="font-['Outfit'] mt-0.5" style={{ fontSize: 12.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.78)' }}>{text}</p>
  </div>
);

const GuideCard = ({ t, i, Icon, open, onToggle, onOpenTile }) => (
  <div className="rounded-2xl overflow-hidden" data-testid={`menu-guide-${t.id}`}
    style={{ background: 'linear-gradient(180deg, rgba(20,16,8,0.9), rgba(8,7,12,0.95))', border: `1px solid ${open ? t.accent : 'rgba(212,175,55,0.18)'}`,
      boxShadow: open ? `0 0 18px ${t.accent}33` : 'none', transition: 'border-color 0.2s, box-shadow 0.2s' }}>
    <button onClick={onToggle} className="w-full flex items-center gap-3 px-3 py-3 text-left" data-testid={`menu-guide-toggle-${t.id}`}>
      <span className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center"
        style={{ background: '#0b0a10', border: `1px solid ${t.accent}`, boxShadow: `0 0 10px ${t.accent}44` }}>
        {Icon ? <Icon size={22} strokeWidth={1.6} style={{ color: t.accent }}/> : <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.accent }}/>}
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline gap-2">
          <span className="font-['Outfit'] tabular-nums" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>{String(i + 1).padStart(2, '0')}</span>
          <span className="font-['Cinzel'] uppercase" style={{ fontSize: 13.5, letterSpacing: '0.14em', color: '#fff' }}>{t.name}</span>
        </span>
        <span className="block font-['Cormorant_Garamond'] italic" style={{ fontSize: 14, color: t.accent }}>{t.tag}</span>
      </span>
      <ChevronDown size={16} color={GOLD_HI} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}/>
    </button>
    {open && (
      <div className="px-4 pb-4 -mt-1">
        <div className="h-px mb-1" style={{ background: `linear-gradient(90deg, transparent, ${t.accent}66, transparent)` }}/>
        <Section label="What it is" text={t.what} color={GOLD_HI}/>
        <Section label="How it works" text={t.how} color={GOLD_HI}/>
        <Section label="Why it matters" text={t.why} color={GOLD_HI}/>
        <button onClick={() => onOpenTile(t.id)} data-testid={`menu-guide-open-${t.id}`}
          className="mt-3 w-full py-2.5 rounded-full font-['Cinzel'] uppercase active:scale-[0.98]"
          style={{ background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0806', fontSize: 11, letterSpacing: '0.24em', fontWeight: 700 }}>
          Open {t.name}
        </button>
      </div>
    )}
  </div>
);

/* Inscribe → Menu: premium guide to all 24 tiles. */
export const TileGuideMenu = ({ icons, onClose, onOpenTile, onOpenPremium }) => {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="fixed inset-0 z-[230] flex flex-col" style={{ background: '#05050A' }} data-testid="menu-guide">
      <div className="flex-shrink-0 flex items-center justify-between px-4 pt-3 pb-2" style={{ borderBottom: '1px solid rgba(212,175,55,0.18)' }}>
        <span className="w-8"/>
        <div className="text-center">
          <div className="font-['Cinzel'] uppercase" style={{ color: GOLD_HI, fontSize: 15, letterSpacing: '0.34em', paddingLeft: '0.34em' }}>Bliss Guide</div>
          <div className="font-['Cormorant_Garamond'] italic" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>24 rituals · what, how & why</div>
        </div>
        <button onClick={onClose} data-testid="menu-guide-close" className="p-2 -mr-2 active:scale-90"><X size={20} color={GOLD_HI}/></button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-2.5" data-testid="menu-guide-list">
        <p className="font-['Outfit'] text-center px-4 pb-1" style={{ fontSize: 12, lineHeight: 1.6, color: 'rgba(255,255,255,0.55)' }}>
          Start with the 8 pillars, your sigil and Aura ring, then tap any ritual to learn what it is, how it works and why it matters.
        </p>
        {onOpenPremium && (
          <button onClick={onOpenPremium} data-testid="menu-open-premium"
            className="w-full rounded-2xl px-4 py-3 text-left flex items-center justify-between active:scale-[0.99]"
            style={{ background: 'linear-gradient(120deg, rgba(212,175,55,0.22), rgba(8,7,12,0.95) 70%)', border: '1px solid #D4AF37', boxShadow: '0 0 16px rgba(212,175,55,0.25)' }}>
            <span>
              <span className="block font-['Cinzel'] uppercase" style={{ color: '#F5D97A', fontSize: 13, letterSpacing: '0.24em' }}>Bliss Premium · ₹349</span>
              <span className="block font-['Cormorant_Garamond'] italic text-white/60" style={{ fontSize: 13 }}>19 premium rituals · see what's inside</span>
            </span>
            <span className="font-['Outfit'] uppercase" style={{ color: '#F5D97A', fontSize: 10, letterSpacing: '0.2em' }}>View</span>
          </button>
        )}
        <GuideIntro/>
        {TILE_GUIDE.map((t, i) => (
          <GuideCard key={t.id} t={t} i={i} Icon={icons[t.id]} open={openId === t.id}
            onToggle={() => setOpenId(openId === t.id ? null : t.id)} onOpenTile={onOpenTile}/>
        ))}
        <GuideSociety onClose={onClose}/>
      </div>
    </div>
  );
};

export default TileGuideMenu;
